export const PromptEngExamples = () => {
  const examples = [
    {
      category: "Feature Implementation",
      bad: "Make a login form",
      good: `I'm building a React app with TypeScript and Tailwind CSS. I need a login form component that:
- Has email and password fields with validation
- Uses react-hook-form with zod schema validation
- Shows inline error messages
- Has a loading state during submission
- Follows our existing component patterns (see example below)

[paste example component]

Please include the form component, validation schema, and types.`,
    },
    {
      category: "Debugging",
      bad: "My code doesn't work",
      good: `I'm getting a "Cannot read properties of undefined (reading 'map')" error in my React component.

**Error:** [full error message with stack trace]

**Code:** [paste the relevant code]

**Context:** This component fetches user data from an API and displays it. The error happens on initial render before the data loads.

**What I've tried:** Added optional chaining but the error persists.

What's causing this and how should I fix it properly?`,
    },
    {
      category: "Refactoring",
      bad: "Make this code better",
      good: `Please refactor this component for better:
1. **Performance** - it re-renders too often
2. **Readability** - split into smaller functions
3. **Type safety** - add proper TypeScript types

Current code: [paste code]

Constraints:
- Keep the same API/props interface
- Use React.memo where appropriate
- Add JSDoc comments for complex logic

Explain your changes so I understand the improvements.`,
    },
    {
      category: "Architecture",
      bad: "How do I structure my app?",
      good: `I'm building a SaaS dashboard with:
- React + TypeScript + Vite
- Supabase for auth and database
- Multiple user roles (admin, user, viewer)
- 10+ pages with shared components

Current structure is getting messy. I need advice on:
1. Folder structure for scalability
2. State management approach (currently prop drilling)
3. How to organize API calls and data fetching

What architecture patterns would you recommend? Show example folder structure.`,
    },
  ];

  return (
    <section className="py-8 sm:py-12">
      <h2 className="text-2xl sm:text-3xl font-display font-bold text-foreground mb-6">
        Before & After: Prompt Examples
      </h2>
      <p className="text-muted-foreground mb-8 max-w-3xl">
        See the difference between vague prompts and well-structured ones.
      </p>

      <div className="space-y-8">
        {examples.map((example, index) => (
          <div key={index} className="bg-card rounded-xl border border-border overflow-hidden">
            <div className="bg-muted/50 px-6 py-3 border-b border-border">
              <h3 className="font-semibold text-foreground">{example.category}</h3>
            </div>
            <div className="grid md:grid-cols-2">
              <div className="p-6 border-b md:border-b-0 md:border-r border-border">
                <div className="text-sm font-medium text-destructive mb-2">❌ Bad Prompt</div>
                <div className="text-sm p-3 rounded-lg" style={{ backgroundColor: 'hsl(0 85% 97%)', color: 'hsl(0 70% 35%)', border: '1px solid hsl(0 70% 90%)' }}>
                  {example.bad}
                </div>
              </div>
              <div className="p-6">
                <div className="text-sm font-medium text-chart-4 mb-2">✓ Good Prompt</div>
                <pre className="text-sm p-3 rounded-lg whitespace-pre-wrap overflow-x-auto" style={{ backgroundColor: 'hsl(142 60% 95%)', color: 'hsl(142 50% 25%)', border: '1px solid hsl(142 40% 85%)' }}>
                  {example.good}
                </pre>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
